/* eslint-disable @typescript-eslint/no-shadow */
import React, { memo } from 'react';
import { Popover } from 'antd';
import type { ProductRightsDescItem, ProductRightsSubTipItem } from '$/api/server.types';
import IconFont from '$/components/IconFont';
import classNames from 'classnames';
import Image from '$/components/NextImage';
// import order1 from '../SingleCategoryItem/order-1.png';
import RadishStep from '../RadishStep';
import { VIP_MODAL_CHILD_Z_INDEX } from '../constants';
import styles from './index.module.scss';

type PricingBadgeTone = 'pricingBadge1' | 'pricingBadge2';

interface CategoryItemProps {
  name: string;
  nameClass?: string;
  moneySymbol: string;
  price: string;
  originalPrice?: string;
  discount?: string;
  discountTone?: PricingBadgeTone;
  operatorNode?: React.ReactNode;
  extraPriceNode?: React.ReactNode;
  monthUnit?: string;
  calcPowerIcon?: string;
  calcPowerTitle?: string;
  calcPowerRate?: string;
  showCalcPowerStep?: boolean;
  calcPowerOptions?: Array<{
    key: string;
    label: string;
    active?: boolean;
    onClick?: () => void;
  }>;
  renewNote?: string;
  productRightsDesc?: ProductRightsDescItem[];
  teamMode?: boolean;
}

/**
 * 判断权益分组是否包含可见内容，过滤接口兜底下发的空分组。
 */
function isVisibleRightsGroup(item: ProductRightsDescItem) {
  return Boolean(item?.title || item?.titleIcon || item?.subTitle?.trim() || item?.subTipItems?.length);
}

/**
 * 渲染新版会员订阅套餐卡，卡片视觉由列表渲染顺序决定。
 */
function CategoryItem(props: CategoryItemProps) {
  const {
    name = '', price = '', discount = '', discountTone = 'pricingBadge1', moneySymbol, operatorNode, extraPriceNode, monthUnit, nameClass, originalPrice = '', calcPowerIcon = '', calcPowerTitle = '', calcPowerRate = '', showCalcPowerStep = false, calcPowerOptions = [], renewNote = '', productRightsDesc = [], teamMode = false,
  } = props;
  const visibleRightsDesc = productRightsDesc.filter(isVisibleRightsGroup);

  /**
   * 按新结构展示权益文案，有气泡说明时提供固定问号图标及悬浮说明。
   */
  const renderRightsRow = (subTip: ProductRightsSubTipItem) => (
    <div className={styles['rights-row']} key={subTip.id}>
      <IconFont name="checkV2Bold" fontSize={12} className={styles['rights-check-icon']} />
      <div className={styles['rights-row-text']}>{subTip.text}</div>
      {subTip.bubbleText?.trim() ? (
        <Popover
          trigger={['hover', 'focus']}
          placement="bottom"
          autoAdjustOverflow
          destroyTooltipOnHide
          overlayClassName={styles['rights-bubble-overlay']}
          overlayStyle={{ zIndex: VIP_MODAL_CHILD_Z_INDEX }}
          content={(
            <div className={styles['rights-bubble']}>
              {/* 活动说明的固定文案暂用中文，待 Smartcat 提供翻译 key 后接入。 */}
              <div className={styles['rights-bubble-title']}>活动说明</div>
              <div className={styles['rights-bubble-text']}>{subTip.bubbleText}</div>
              <div className={styles['rights-bubble-footer']}>最终以正式活动规则及页面实际展示为准</div>
            </div>
          )}
        >
          <button type="button" className={styles['rights-bubble-trigger']} aria-label={`${subTip.text}：活动说明`}>
            <IconFont name="questionMarkCircleBold" fontSize={14} />
          </button>
        </Popover>
      ) : null}
    </div>
  );

  /**
  * 渲染接口权益分组，限时活动的权益内容使用独立高亮框，标题保留在框外。
  */
  const renderRightsGroup = (item: ProductRightsDescItem, index: number) => {
    // 商品 Hook 已将接口 item_category 转为 itemCategory，仅限时活动启用高亮。
    const isLimitedTimePromotion = item.itemCategory === 'limit_time_promotion';
    const titleIconNode = item.titleIcon
      ? <Image src={item.titleIcon} width={16} height={16} alt="" />
      : null;

    return (
      // eslint-disable-next-line react/no-array-index-key
      <div className={styles['tip-section']} key={`${item.itemCategory || item.title || 'rights'}-${index}`}>
        {item.title ? (
          <div className={classNames(styles['tip-section-title'], {
            [styles['promotion-tip-section-title']]: isLimitedTimePromotion,
          })}
          >
            {titleIconNode}
            <span>{item.title}</span>
          </div>
        ) : null}
        {item.subTitle?.trim() || item.subTipItems?.length ? (
          <div className={classNames(styles['rights-group-content'], {
            [styles['promotion-rights-content']]: isLimitedTimePromotion,
          })}
          >
            {item.subTitle?.trim() ? <div className={styles['rights-subtitle']}>{item.subTitle}</div> : null}
            {item.subTipItems?.length ? (
              <div className={styles['rights-section-content']}>
                {item.subTipItems.map(renderRightsRow)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={classNames(styles['category-item'], styles['subscription-item'], {
      [styles['team-subscription-item']]: teamMode,
    })}
    >
      {discount && (
        <div className={classNames(styles.discount, {
          [styles['discount-pricing-badge-2']]: discountTone === 'pricingBadge2',
        })}
        >
          {discount}
        </div>
      )}
      <div className={classNames(styles['category-item-name'], nameClass)}>
        <div>{name}</div>
      </div>
      <div className={styles['category-item-price-info']}>
        <div className={styles['category-item-price-symbol']}>{moneySymbol}</div>
        <div className={styles['category-item-price']}>{price}</div>
        <div className={styles['category-item-unit-text']}>{monthUnit}</div>
        {originalPrice && (
          <div className={styles['category-item-originla-price']}>
            {originalPrice}
          </div>
        )}
      </div>
      <div className={styles['renew-note']}>{renewNote}</div>
      {extraPriceNode}
      <RadishStep
        icon={calcPowerIcon}
        title={calcPowerTitle}
        rate={calcPowerRate}
        options={calcPowerOptions}
        showStep={showCalcPowerStep}
        className={classNames({ [styles['calc-power-card-with-extra']]: Boolean(extraPriceNode) })}
      />
      <div className={styles['operate-btn']}>{operatorNode}</div>
      <div className={styles['category-tips']}>
        {visibleRightsDesc.map(renderRightsGroup)}
      </div>
    </div>
  );
}
export default memo(CategoryItem);
